import type { Section, Task } from './models'

const DAY_MS = 86_400_000

/**
 * **Oberste Ebene** — keine **Unteraufgabe**. Seit Unteraufgaben Aufgaben sind (T14 #179),
 * entscheidet diese eine Regel, wo sie NICHT erscheinen: Projektseite, Kurz notiert,
 * Fortschrittskreis. Dort stehen sie in der Checkliste ihrer Aufgabe. **Demnächst** zeigt
 * sie mit eigenem Datum (`isInDateRange`, `isPlannedWithinNextWeek` prüfen das bewusst
 * nicht); seit #205 außerdem **Ruhend**, wenn sie selbst ruhen, **Wiederkehrend**, wenn sie
 * sich selbst wiederholen, und **Geschafft** ihre erledigten Runden (`isCompletedTask`).
 */
export function isTopLevel(task: Task): boolean {
  return task.parentTaskId == null
}

/** Eine Aufgabe der **Projektseite**: im Projekt, nicht gelöscht, oberste Ebene. */
export function isProjectTask(task: Task, projectId: string): boolean {
  return task.projectId === projectId && !task.deletedAt && isTopLevel(task)
}

/**
 * Ein Eintrag in **Kurz notiert** (Quick Notes): ohne Projekt, ohne Start, nicht
 * ruhend, nicht gelöscht. Erledigte zählen mit — sie liegen dort hinter dem
 * **Geschafft-Schalter** (CONTEXT.md). Den Lebensbereich prüft der Aufrufer.
 *
 * Eine Regel für Dexie (`getQuickNotesByLifeSpace`) und die Speicher-Quelle.
 */
export function isQuickNote(task: Task): boolean {
  return task.projectId == null && !task.deletedAt && task.pausedAt == null && task.startDate == null && isTopLevel(task)
}

/**
 * Offen, nicht ruhend und auf heute oder einen der nächsten sieben Tage
 * **eingeplant** (Starttag). Die Zahl am Sidebar-Eintrag „Demnächst"
 * und auf dem Dock-Symbol.
 *
 * Bis 22.09.2026 zählte sie nur Deadlines: Wer eine Aufgabe auf morgen legte,
 * sah sie auf der Seite, aber nicht in der Zahl. Heute zählt seitdem mit, weil
 * die Seite „Demnächst" heute auch zeigt.
 *
 * Eine Regel für Dexie (`getUpcomingTaskCountByLifeSpace`) und die Speicher-Quelle.
 */
export function isPlannedWithinNextWeek(task: Task, now: Date = new Date()): boolean {
  if (task.deletedAt || task.status === 'completed' || task.pausedAt != null) return false
  if (!task.startDate) return false
  const todayMs = new Date(now).setHours(0, 0, 0, 0)
  const start = new Date(task.startDate).setHours(0, 0, 0, 0)
  const dayDiff = Math.round((start - todayMs) / DAY_MS)
  return dayDiff >= 0 && dayDiff <= 7
}

/**
 * **Ruhend**: pausiert, nicht gelöscht. Erledigte kommen mit — sie liegen hinter dem
 * **Geschafft-Schalter** (CONTEXT.md). Den Lebensbereich prüft der Aufrufer.
 *
 * **Auch Unteraufgaben, die selbst ruhen** (#205): Welche davon die Seite zeigt, entscheidet
 * ihre Aufgabe (`parentKeepsChildListed`) — ruht die, steht nur sie da.
 */
export function isDormantTask(task: Task): boolean {
  return !task.deletedAt && task.pausedAt != null
}

/**
 * **Wiederkehrend**: mit Wiederholung, ruhend oder nicht, nicht gelöscht. Erledigte kommen mit —
 * die Seite blendet sie erst nach der **Liegezeit** aus. **Auch Unteraufgaben mit eigenem
 * Rhythmus** (#205); welche die Seite zeigt, entscheidet ihre Aufgabe (`parentKeepsChildListed`).
 */
export function isRecurringTask(task: Task): boolean {
  return !task.deletedAt && task.recurrence != null
}

/**
 * **Geschafft**: mit Erledigt-Zeitpunkt, nicht gelöscht, oberste Ebene — abgehakte
 * Unteraufgaben zählen weder dort noch in der **Serie** (Sven, 27.09.2026). Ausnahme sind die
 * **erledigten Runden einer wiederkehrenden Unteraufgabe** (#205): Welche davon zählen, sagt
 * `isCompletedRound` an ihrer Aufgabe. Dexie fragt über den Index auf `completedAt`.
 */
export function isCompletedTask(task: Task): boolean {
  return !task.deletedAt && task.completedAt != null && (isTopLevel(task) || task.recurrence != null)
}

/**
 * **Eine erledigte Runde einer wiederkehrenden Unteraufgabe** (#205, Punkt 7 und 9) — selbst
 * abgehakt, nicht mit ihrer Aufgabe: Die Kopplung hakt mit demselben Zeitpunkt ab wie die
 * Aufgabe, und dann steht die Aufgabe in Geschafft, nicht jede ihrer Zeilen.
 */
export function isCompletedRound(child: Task, parent: Task): boolean {
  if (parent.deletedAt || !child.completedAt) return false
  const together = parent.status === 'completed' && parent.completedAt != null
    && new Date(parent.completedAt).getTime() === new Date(child.completedAt).getTime()
  return !together
}

/** Wie lange nach dem Abhaken ein Nachfolger noch als der dieser Runde gilt (in ms). */
const SUCCESSOR_WINDOW_MS = 60_000

/**
 * **Die Checkliste zeigt nur die aktuelle Runde** (#205, Punkt 7): Eine erledigte wiederkehrende
 * Unteraufgabe verlässt sie, sobald ihr Nachfolger dasteht — sie steht dann in Geschafft.
 *
 * Nachfolger ist eine andere, nicht gelöschte Unteraufgabe derselben Aufgabe, die beim Abhaken
 * entstand (`SUCCESSOR_WINDOW_MS`). Ein eigenes Feld für die Verbindung gibt es nicht; der
 * Zeitpunkt hält auch, wenn jemand die neue Runde umbenennt oder ihr den Rhythmus nimmt (selbst
 * oder weil die Aufgabe wiederkehrend wird) — sonst stünden alle alten Runden wieder da.
 * Mitgehakt (Kopplung) entsteht keiner, dann bleibt die Zeile — ebenso die letzte Runde einer
 * endenden Wiederholung.
 * `⌘Z` löscht den Nachfolger, und die Runde steht wieder da.
 *
 * `family`: wo der Nachfolger stehen darf — **der ganze Baum der Aufgabe** (#181). Ein Nachfolger,
 * der eingerückt oder ausgerückt wurde, steht nicht mehr neben seiner alten Runde; nur unter den
 * Geschwistern gesucht, tauchte sie dann wieder auf (gemeldet 28.09.2026). Ohne Angabe: die
 * Geschwister.
 */
export function withoutSucceededRounds<T extends Task>(children: readonly T[], family: readonly Task[] = children): T[] {
  return children.filter(child => {
    if (child.recurrence == null || child.status !== 'completed' || !child.completedAt) return true
    const done = new Date(child.completedAt).getTime()
    return !family.some(other => {
      if (other.id === child.id || other.deletedAt) return false
      const created = new Date(other.createdAt).getTime()
      return created >= done && created - done <= SUCCESSOR_WINDOW_MS
    })
  })
}

/**
 * **Die alten Runden einer Zeile** (#181): erledigte wiederkehrende Zeilen aus `family`, deren
 * Nachfolger sie ist — rückwärts die Kette entlang (Runde von gestern, vorgestern, …), nach derselben
 * Regel wie {@link withoutSucceededRounds}. Wer die Zeile in eine andere Aufgabe hängt oder
 * herauslöst, nimmt sie mit; sonst stünden sie in der alten Checkliste wieder da.
 *
 * Wurden zwei Runden im selben Moment abgehakt, gehört die gleichnamige dazu, sonst die zuletzt
 * abgehakte.
 */
export function roundHistoryOf<T extends Task>(family: readonly T[], row: Task): T[] {
  const history: T[] = []
  const taken = new Set([row.id])
  let current: Task = row
  for (;;) {
    const created = new Date(current.createdAt).getTime()
    const candidates = family.filter(t => {
      if (taken.has(t.id) || t.deletedAt || t.recurrence == null || t.status !== 'completed' || !t.completedAt) return false
      const done = new Date(t.completedAt).getTime()
      return created >= done && created - done <= SUCCESSOR_WINDOW_MS
    })
    if (candidates.length === 0) return history
    const latest = (a: T, b: T) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime()
    const round = candidates.find(t => t.title === current.title) ?? [...candidates].sort(latest)[0]!
    history.push(round)
    taken.add(round.id)
    current = round
  }
}

/**
 * Liegt im Zeitraum `[from, to)` von **Demnächst**: der Beginn, oder ohne Beginn die Fälligkeit.
 * Eine Aufgabe mit Beginn davor zählt nicht, auch wenn sie im Zeitraum fällig ist. Dexie rechnet
 * das mit zwei Bereichsabfragen (Beginn; Fälligkeit ohne Beginn).
 */
export function isInDateRange(task: Task, from: Date, to: Date): boolean {
  if (task.deletedAt) return false
  const date = task.startDate ?? task.dueDate
  if (!date) return false
  const ms = new Date(date).getTime()
  return ms >= from.getTime() && ms < to.getTime()
}


/**
 * **Steht eine Unteraufgabe außerhalb ihrer Checkliste?** Nur, solange ihre Aufgabe da, offen
 * und wach ist — sie ruht, endet und geht mit ihr (T14 #179, Grill 5 und 7). Ruht die Aufgabe,
 * zeigt **Ruhend** nur sie (#205). Eine Regel für **Demnächst** (Seite, Zahl in Sidebar und
 * Dock) und **Ruhend**.
 */
export function parentKeepsChildListed(parent: Task | undefined): boolean {
  return parent != null && !parent.deletedAt && parent.pausedAt == null && parent.status !== 'completed'
}

/**
 * **Reicht die Kette bis zur Aufgabe?** (#181) Die Kette über einer Unteraufgabe, vom Eltern-Teil
 * hinauf. Fehlt ein Glied (noch nicht synchronisiert, ein Zyklus), steht die Zeile nirgends —
 * wie bisher bei einem fehlenden Eltern-Teil.
 */
export function isWholeChain(chain: readonly Task[]): boolean {
  return chain.length > 0 && chain[chain.length - 1]!.parentTaskId == null
}

/** `parentKeepsChildListed` für eine tiefe Unteraufgabe: jede Ebene darüber muss sie lassen (#181). */
export function chainKeepsChildListed(chain: readonly Task[] | undefined): boolean {
  return chain != null && isWholeChain(chain) && chain.every(parentKeepsChildListed)
}

/**
 * **Vom Archivieren abgehakt** (#193): erledigt in diesem Abschnitt, und zwar genau zum Zeitpunkt
 * seiner Archivierung — `archiveSection` schreibt beiden denselben Zeitpunkt. So erkennt
 * „Mit Aufgaben wiederherstellen“ sie auch später noch, ohne eigenes Feld; was vorher erledigt
 * war, trägt einen anderen Zeitpunkt. Unteraufgaben zählen mit.
 */
export function isClosedByArchive(task: Task, section: Section): boolean {
  if (!section.archivedAt || !task.completedAt) return false
  return task.sectionId === section.id && !task.deletedAt && task.status === 'completed'
    && new Date(task.completedAt).getTime() === new Date(section.archivedAt).getTime()
}

